import EdPresentationModeMixin from '@/content/mixins/ed-presentation-mode.mixin';
import { CHANGE_PRESENTATION_TASK_USER_EVENT, STATUS_CLASSES, FILTER_PRESENTATION_USERS_EVENT } from '@/content/values/ed-presentation-mode.values';
import { IRequestHomeworkStatusByStudent } from '@/homework/interfaces/request-homework-status-by-student.interface';
import { HomeworkStatusStudent } from '@/homework/models/homework-status-student-response.model';
import { HOMEWORKS_STORE } from '@/homework/store/homeworks.store';
import { HOMEWORK_STATUS } from '@/homework/values/homeworks.values';
import { MEETINGS_STORE } from '@/meetings/store/meetings.store';
import { EdContextMenuModel, EdContextMenuOptionsModel } from '@/plugins/ed-context-menu/ed-context-menu.plugin';
import Component from 'vue-class-component';
import { Mixins, Prop } from 'vue-property-decorator';
import KEYS from '@/values/keys';
import { COURSE_EVENTS } from '@/content/values/events';
import { TYPE_TEXT_DRAFT_RESPONSE_OPTIONS } from '@/content/values/sections';

@Component({})
export default class EdPresentationModeUsers extends Mixins(EdPresentationModeMixin) {

  @Prop()
  public sectionId : number;

  @Prop()
  public sectionType: string;

  @Prop()
  public courseId : number;

  public isLoading : boolean = false;
  private options : {[key: string]: any}[] = [];
  private orignalOptions : {[key: string]: any}[] = [];
  private onlineColor: string = 'gray';
  private offlineColor: string = 'lightgray';
  private iconOnline: string = 'user';
  private iconOffline:  string = 'user-slash';

  get currentUsers() : {[key: string]: any}[] {
    return this.$store.state.MeetingsStore.currentMeetingUsers;
  }

  get currentActiveUsers() : string[] {
    return this.$store.state.MeetingsStore.currentActiveUsers;
  }

  get isDisable() : boolean {
    return this.sectionForCourse(this.courseId)?.sectionId !== this.sectionId || !this.isWhiteboardTransparency;
  }

  public async openUserMenu(e: MouseEvent) : Promise<void> {
    if(this.isDisable){
      return;
    }
    this.isLoading = true;
    this.options = this.buildMenuOptions();
    this.orignalOptions = [...this.options];
    try {
      await this.callHomeworkStatus();
      this.showUsers(e);
    } catch (e) {
      this.$EdModal.createMessageModal(
        {
          title: this.$i18n.t('common.information'),
          body: this.$i18n.t('errors.errorLoading'),
          type: KEYS.EVENT_TYPES.DANGER
        }
      )
    }
  }

  private showUsers(e: MouseEvent) {
    this.$EdContextMenu.show(new EdContextMenuModel({
      position: { x: e.pageX, y: e.clientY },
      options: this.options,
      headerComponent: 'EdPresentationModeUserFilter'
    })).then((optionSelected: EdContextMenuOptionsModel) => {
      if (!optionSelected || !optionSelected.id || optionSelected.disabled 
        || this.validateIsSameUser(optionSelected)) {
        return;
      }

      const isTeacher: boolean = this.userLoginId == optionSelected.id;
      this.$store.commit(MEETINGS_STORE.MUTATIONS.UPDATE_CURRENT_BOARD_USER, {
        id: optionSelected.id, fullName: optionSelected.text, isTeacher: isTeacher, sectionId: this.sectionId
      });
      this.$store.commit(HOMEWORKS_STORE.MUTATIONS.CLEAR_PRESENTATION_SECTION_CONTENT);
      this.$edEventBus.$emit(COURSE_EVENTS.CHANGE_ZOOM_VALUE, true);
    });
  }

  private validateIsSameUser(optionSelected: EdContextMenuOptionsModel) : boolean {
    return this.currentBoardUserId === optionSelected.id || (!this.currentBoardUserId && optionSelected.id === this.userLoginId);
  }

  private buildMenuOptions() : {[key: string]: any}[] {

    let allUsers : any[] = [{
        id: this.userLoginId,
        text: this.$t('myContent.teacher'),
        disabled: false,
        isActive: true,
        isAlerted: true,
        showAsDisable: false,
        icon: this.iconOnline,
        color: this.onlineColor,
        textBoldClass: this.currentBoardUserId === this.userLoginId || !this.currentBoardUserId
      }
    ];
    
    return allUsers.concat(this.currentUsers.map(user => {
      let isActiveUser : boolean = this.currentActiveUsers.includes(user.username);
      return {
        id: user.id,
        text: user.fullName,
        disabled: false,
        isActive: isActiveUser,
        isAlerted: true,
        showAsDisable: !isActiveUser,
        icon: (isActiveUser) ? this.iconOnline : this.iconOffline,
        color: (isActiveUser) ? this.onlineColor : this.offlineColor, 
        alertType: STATUS_CLASSES.DRAFT_EMPTY.className,
        title: STATUS_CLASSES.DRAFT_EMPTY.title,
        textBoldClass: this.currentBoardUserId === user.id
      };
    }));
  }

  private callHomeworkStatus(): Promise<void> {
    let activeUserIds: number[] = this.currentUsers.map(user => user.id);

    if (activeUserIds.length === 0) {
      this.isLoading = false
      return;
    }

    let activeUserIdsString = btoa(JSON.stringify(activeUserIds));

    const currentSection = this.sectionForCourse(this.courseId);
    const request: IRequestHomeworkStatusByStudent = {
      classId: currentSection.classId,
      contentId: currentSection.courseId,
      sectionId: currentSection.sectionId,
      studentIds: activeUserIdsString
    };
    return this.$store.dispatch(HOMEWORKS_STORE.ACTIONS.OBTAIN_STUDENTS_HOMEWORK_STATUS, request).then((response) => {
      response.forEach((studentStatus: HomeworkStatusStudent) => {
        let userIndex: number = this.options.findIndex(option => option.id === studentStatus.studentId);
        if (userIndex >= 0) {
          this.options[userIndex].isAlerted = true;
          let statusType = studentStatus.status === HOMEWORK_STATUS.DRAFT && studentStatus.homeworkDraftEmpty && studentStatus.boardAvailable
            ? STATUS_CLASSES.DRAFT_EMPTY
            : studentStatus.status === HOMEWORK_STATUS.DRAFT && (!studentStatus.boardAvailable && !TYPE_TEXT_DRAFT_RESPONSE_OPTIONS.includes(this.sectionType))
            ? STATUS_CLASSES.NO_AVAILABLE
            : STATUS_CLASSES[studentStatus.status];
          this.options[userIndex].alertType = statusType.className;
          this.options[userIndex].title = statusType.title;
          this.options[userIndex].disabled = statusType.disabled;
        }
      });
    }).finally(() => this.isLoading = false);
  }

  private filterPresentationUsers(isShowAll: boolean): void {
    if (this.courseId !== this.currentPresentationSection.courseId) {
      return;
    }
    if (this.isDisable) {
      return;
    }
    this.options = (isShowAll) ? [...this.orignalOptions] : this.orignalOptions.filter(opt => opt.isActive);
    this.$EdContextMenu.updateOptions(this.options);
  }

  mounted() {
    this.$edEventBus.$on(FILTER_PRESENTATION_USERS_EVENT, this.filterPresentationUsers);
  }

  beforeDestroy() {
    this.$store.commit(MEETINGS_STORE.MUTATIONS.UPDATE_CURRENT_BOARD_USER, {});
    this.$edEventBus.$off(FILTER_PRESENTATION_USERS_EVENT, this.filterPresentationUsers );
  }

}